import React, { useEffect, useMemo } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { CheckIcon, CaretUpIcon, CaretDownIcon, XIcon, MagnifyingGlassIcon, PlusIcon } from '@phosphor-icons/react';

import classNames from '@utils/classnames';
import { Spinner } from 'components/Spinner';
import { controlClassName, popoverItemClassName, popoverPanelClassName } from '../form/control';

interface ISelectItemProps {
  children: React.ReactNode;
  value: string;
  selected: boolean | null;
  className?: string;
  onClick?: () => void;
}

const SelectItem: React.FC<ISelectItemProps> = (props) => {
  const { children, selected, className, onClick } = props;

  return (
    <div className={classNames(popoverItemClassName, className)} onClick={onClick}>
      <div className="min-w-0 truncate">{children}</div>
      {selected && (
        <div className="absolute inset-y-0 right-0 flex items-center pr-2.5 text-brand-text">
          <CheckIcon className="h-4 w-4" weight="bold" aria-hidden="true" />
        </div>
      )}
    </div>
  );
};

export interface IComboboxProps<V extends Record<string, any>> {
  placeholder?: string;
  inputPlaceholder?: string;
  onQueryChange: (newQuery: string) => void;
  items: V[];
  selectedItem?: V | null;
  onSelect: (value: V | null) => void;
  keyName: string;
  display: (value: V) => string;
  isDisabled?: boolean;
  isInvalid?: boolean;
  isLoading?: boolean;
  // TODO: declared but never wired up — Formik `touched` is never set by a combobox.
  onBlur?: () => void;
  isNullable?: boolean;
  /**
   * Renders a pinned "create new" row at the bottom of the dropdown, e.g. 'Maak klant aan'.
   * Creating is not selecting: this row never reaches `onSelect`, never touches
   * `onQueryChange` and never participates in `selectedItem` comparison.
   */
  createLabel?: string;
  /**
   * Fired once the popover has *fully* unmounted, so it is safe to open a modal dialog
   * from here. Receives the search query at the moment of the click.
   */
  onCreate?: (query: string) => void;
}

export function Combobox<V extends Record<string, any>>(props: IComboboxProps<V>) {
  const {
    placeholder = 'Selecteer een item',
    inputPlaceholder = 'Zoeken...',
    keyName,
    onQueryChange,
    items,
    display,
    selectedItem,
    onSelect,
    isDisabled,
    isInvalid,
    isNullable,
    isLoading,
    createLabel,
    onCreate,
  } = props;
  const [isOpen, setIsOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');

  /**
   * Holds the search query while a "create" click is in flight, and is `null` otherwise.
   * A ref rather than state: it has to survive the popover's unmount, and a re-render
   * would restart Radix's exit lifecycle. The null sentinel also absorbs StrictMode's
   * double-invoked FocusScope cleanup.
   */
  const pendingCreateQueryRef = React.useRef<string | null>(null);
  const showCreateRow = Boolean(createLabel && onCreate && !isDisabled);

  const displayValue = useMemo(() => {
    return selectedItem ? display(selectedItem) : '';
  }, [selectedItem]);

  useEffect(() => {
    onQueryChange(query);
  }, [query]);

  const CaretIcon = isOpen ? CaretUpIcon : CaretDownIcon;

  return (
    <div className="flex w-full gap-1">
      <Popover.Root
        open={isOpen}
        modal={true}
        onOpenChange={(newOpen) => {
          if (!newOpen) {
            setTimeout(() => {
              setIsOpen(false);
            }, 1);
          } else {
            setQuery('');
            onQueryChange('');
            setIsOpen(true);
          }
        }}
      >
        <Popover.Trigger asChild disabled={isDisabled}>
          <button
            type="button"
            className={classNames(
              controlClassName({ isInvalid, isDisabled, focusWithin: false }),
              'flex items-center gap-2 px-2.5 text-left',
              isOpen && !isInvalid && !isDisabled && 'border-brand',
            )}
          >
            <span
              className={classNames('min-w-0 flex-1 truncate', {
                'text-fg': !isDisabled && displayValue,
                'text-fg-muted': isDisabled || !displayValue,
              })}
            >
              {isLoading ? <Spinner size={4} color="default" /> : displayValue || placeholder}
            </span>
            <CaretIcon className="h-4 w-4 shrink-0 text-fg-muted" aria-hidden="true" />
          </button>
        </Popover.Trigger>

        <Popover.Portal>
          <Popover.Content
            align="start"
            autoFocus={false}
            className={classNames(popoverPanelClassName, 'mt-1 max-h-60 w-full py-0 pointer-events-auto')}
            style={{
              minWidth: 'var(--radix-popover-trigger-width)',
              maxHeight: 'var(--radix-popover-content-available-height)',
            }}
            /**
             * Radix dispatches this from a `setTimeout(0)` in FocusScope's unmount cleanup —
             * i.e. after the DismissableLayer restored `document.body` pointer-events, after
             * `hideOthers()` was undone, after RemoveScroll unwound, and after the panel left
             * the DOM. It is therefore the only point where opening a modal dialog cannot
             * overlap with this modal popover (which paints at z-dropdown 1000, above
             * z-dialog 900, and whose FocusScope would otherwise steal the dialog's focus).
             *
             * Deliberately no `preventDefault()`: letting Radix focus the trigger first means
             * the dialog records it as `previouslyFocusedElement`, so focus returns here on close.
             */
            onCloseAutoFocus={() => {
              const pendingQuery = pendingCreateQueryRef.current;
              if (pendingQuery === null) return;
              pendingCreateQueryRef.current = null;
              onCreate?.(pendingQuery);
            }}
          >
            <div className="sticky top-0 flex items-center gap-2 border-b border-border bg-surface px-2.5">
              <MagnifyingGlassIcon className="h-4 w-4 shrink-0 text-fg-muted" />
              <input
                className="h-9 w-full bg-transparent text-sm text-fg outline-hidden"
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                }}
                onClick={(evt) => {
                  evt.stopPropagation();
                  evt.preventDefault();
                }}
                placeholder={inputPlaceholder}
                autoComplete="off"
              />
            </div>

            <div className="py-1">
              {items.length ? (
                items.map((item) => (
                  <SelectItem
                    key={item[keyName]}
                    value={item[keyName]}
                    selected={Boolean(selectedItem && item[keyName] === selectedItem[keyName])}
                    onClick={() => {
                      if (isDisabled) return;

                      if (selectedItem === item && isNullable) {
                        onSelect(null);
                        onQueryChange('');
                      } else {
                        onSelect(item);
                        onQueryChange(display(item));
                        setTimeout(() => {
                          setIsOpen(false);
                        }, 1);
                      }
                    }}
                  >
                    {display(item)}
                  </SelectItem>
                ))
              ) : (
                <div className="px-3 py-2 text-sm text-fg-muted">Geen items gevonden</div>
              )}
            </div>

            {showCreateRow && (
              <div className="sticky bottom-0 border-t border-border bg-surface">
                <button
                  type="button"
                  className="flex w-full items-center gap-2 px-2.5 py-2 text-left text-sm font-medium text-brand-text outline-hidden hover:bg-surface-hover focus-visible:bg-surface-hover"
                  onClick={() => {
                    if (isDisabled) return;

                    // Remember the query for prefilling, then close synchronously. We do not
                    // call onSelect/onQueryChange — creating is not selecting.
                    pendingCreateQueryRef.current = query;
                    setIsOpen(false);
                  }}
                >
                  <PlusIcon className="h-4 w-4 shrink-0" weight="bold" aria-hidden="true" />
                  <span className="min-w-0 truncate">{createLabel}</span>
                </button>
              </div>
            )}
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>

      {Boolean(selectedItem && isNullable) && (
        <button
          type="button"
          aria-label="Wis selectie"
          className="flex h-9 w-8 shrink-0 items-center justify-center rounded-md text-fg-muted transition-colors hover:bg-surface-hover hover:text-fg"
          onClick={(evt) => {
            evt.stopPropagation();
            evt.preventDefault();

            onSelect(null);
            onQueryChange('');
          }}
        >
          <XIcon className="button-icon" />
        </button>
      )}
    </div>
  );
}
